import { IPlan, PLAN_TYPE, TPLAN_TYPE } from '@/types/entities/subscriptions.entities';

/** Version-free copy for loading/legacy plans; never invent model entitlements. */
export const getModelAccessFallback = (type: TPLAN_TYPE): string => {
  switch (type) {
    case PLAN_TYPE.FREE:
      return 'AI Access: Fast models';
    case PLAN_TYPE.BASIC:
      return 'AI Access: Advanced models';
    case PLAN_TYPE.PREMIUM:
      return 'AI Access: Frontier models';
    case PLAN_TYPE.ENTERPRISE:
      return 'AI Access: Frontier models + custom options';
    default:
      return 'AI Access';
  }
};

// Group by model family, including models hosted through Bedrock. No version
// numbers here: the catalog's displayOrder decides which models to highlight.
const modelFamily = (name: string): string => {
  if (/^(gpt[ -]|o\d)/i.test(name)) return 'openai';
  return name.toLowerCase().split(/[\s-]/)[0];
};

const catalogAddedAt = (createdAt?: string): number => {
  const timestamp = createdAt ? Date.parse(createdAt) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getPlanModelAccess = (
  plan: Pick<IPlan, 'type'> & Partial<Pick<IPlan, 'modelPlans'>>
): string => {
  const models = (plan.modelPlans ?? [])
    .map((entry) => entry.model)
    .filter((model) => model?.isActive === true && model.name?.trim())
    .sort(
      (a, b) =>
        (a.displayOrder ?? 999) - (b.displayOrder ?? 999) ||
        // New catalog entries can share a display position with their predecessor.
        // Prefer the newer addition instead of alphabetically choosing the old one.
        catalogAddedAt(b.createdAt) - catalogAddedAt(a.createdAt) ||
        b.name.localeCompare(a.name, 'en', { numeric: true })
    );

  const names = Array.from(new Set(models.map((model) => model.name.trim())));
  if (!names.length) return getModelAccessFallback(plan.type);

  const families = new Set<string>();
  const highlights: string[] = [];
  for (const name of names) {
    const family = modelFamily(name);
    if (families.has(family)) continue;
    families.add(family);
    highlights.push(name);
    if (highlights.length === 3) break;
  }

  return `AI Access: ${highlights.join(', ')}${names.length > highlights.length ? ', and more' : ''}`;
};
